import { HlcClock, keyBetween, uuidv7, type Id, type Page, type PageInput } from '@nexus/core';
import type { SqlDriver, SqlValue, Statement } from './driver';
import { migrate } from './migrations';

interface PageRow {
  id: string;
  parent_id: string | null;
  title: string;
  icon: string | null;
  sort_key: string;
  created_at: number;
  updated_at: number;
  trashed_at: number | null;
  hlc: string;
  deleted: number;
  /** A shared workspace this page belongs to, or null for a private page. Set once, at creation. */
  workspace_id: string | null;
  /** Who last pushed content for this page (server-stamped); null for a page no one has shared edits on yet. */
  last_edited_by: string | null;
}

export interface PageRecord extends Page {
  trashedAt: number | null;
  /** A shared workspace this page belongs to, or null for a private page. */
  workspaceId: string | null;
  /** Who last edited this page's content (server-stamped, for shared pages). */
  lastEditedBy: string | null;
}

const toPage = (r: PageRow): PageRecord => ({
  id: r.id,
  parentId: r.parent_id,
  title: r.title,
  icon: r.icon,
  sortKey: r.sort_key,
  createdAt: r.created_at,
  updatedAt: r.updated_at,
  trashedAt: r.trashed_at,
  hlc: r.hlc,
  deleted: r.deleted === 1,
  workspaceId: r.workspace_id,
  lastEditedBy: r.last_edited_by,
});

const SUBTREE_SQL = `WITH RECURSIVE sub(id) AS (
  SELECT ? UNION ALL SELECT p.id FROM pages p JOIN sub ON p.parent_id = sub.id
) SELECT id FROM sub`;

/** The `tags` property of page ?1 as plain text, for the search index. */
const TAGS_TEXT_SQL = `COALESCE((
  SELECT group_concat(t.value, ' ') FROM page_props pp, json_each(pp.value) t
  WHERE pp.page_id = ?1 AND pp.key = 'tags' AND pp.deleted = 0 AND json_valid(pp.value)), '')`;

const reindexTags = (pageId: Id): Statement => ({
  sql: `UPDATE page_fts SET tags = ${TAGS_TEXT_SQL} WHERE page_id = ?1`,
  params: [pageId],
});

type Listener = () => void;

/** What changed, so views only reload what they show. */
export type Topic =
  | 'pages'
  | 'content'
  | 'props'
  | 'index'
  | 'versions'
  | 'attachments'
  | 'tasks'
  | 'notifications'
  /** Workspace settings that sync (special folders, dashboard, …). */
  | 'settings'
  /** The AI activity log. */
  | 'ai'
  /** Shared workspaces (Deel B): the list, or who's a member. */
  | 'workspaces';
const ALL_TOPICS: Topic[] = [
  'pages',
  'content',
  'props',
  'index',
  'versions',
  'attachments',
  'tasks',
  'notifications',
  'settings',
  'workspaces',
];

/**
 * The app's single entry point to local data. Every mutation writes the row and
 * an outbox entry in one transaction, so the (later) sync engine never misses a change.
 */
export class Store {
  private listeners = new Map<Listener, Set<Topic>>();

  private constructor(
    readonly db: SqlDriver,
    readonly deviceId: string,
    private readonly clock: HlcClock,
    private readonly now: () => number,
  ) {}

  static async open(db: SqlDriver, now: () => number = Date.now): Promise<Store> {
    await migrate(db);
    // A restored backup is a copy of another device, device_id included. Two
    // devices sharing one id skip each other's content as "our own" forever,
    // so the copy becomes a device of its own and pulls all content again.
    const [restored] = await db.query<{ key: string }>(
      `SELECT key FROM kv WHERE key IN ('needs_resync', 'reset_device_id') LIMIT 1`,
    );
    if (restored) {
      await db.batch([
        {
          sql: `DELETE FROM kv WHERE key IN ('device_id', 'reset_device_id')
                OR key LIKE 'sync_cursor_content:%'`,
        },
      ]);
    }
    let [row] = await db.query<{ value: string }>(`SELECT value FROM kv WHERE key = 'device_id'`);
    if (!row) {
      row = { value: uuidv7() };
      await db.batch([
        { sql: `INSERT INTO kv(key, value) VALUES ('device_id', ?)`, params: [row.value] },
      ]);
    }
    const store = new Store(db, row.value, new HlcClock(row.value, now), now);
    await store.resyncIfRestored();
    return store;
  }

  /**
   * A restored backup (`restoreBackup`, desktop's `db_restore` or the browser's
   * `importDb`) brings its own outbox/synced flags along -- usually empty,
   * since the backed-up device had already pushed everything. Left alone, the
   * restored content would look fully synced already and never reach the
   * cloud or other devices. Whoever did the restore sets a `needs_resync`
   * marker in `kv`; we see it here (every platform reopens through this same
   * `Store.open()`) and queue every page, prop, setting and attachment as
   * freshly changed, plus every page_updates row as unsent. Safe to repeat:
   * re-pushing already-synced content is a no-op server-side.
   */
  private async resyncIfRestored(): Promise<void> {
    const [marker] = await this.db.query<{ value: string }>(
      `SELECT value FROM kv WHERE key = 'needs_resync'`,
    );
    if (!marker) return;
    const hlc = this.clock.now();
    const pages = await this.db.query<{ id: string }>(`SELECT id FROM pages`);
    const props = await this.db.query<{ page_id: string; key: string }>(
      `SELECT page_id, key FROM page_props`,
    );
    const settings = await this.db.query<{ key: string }>(`SELECT key FROM synced_settings`);
    const attachments = await this.db.query<{ id: string }>(`SELECT id FROM attachments`);
    await this.db.batch([
      ...pages.map((p) => this.outbox(p.id, {}, hlc, 'page')),
      ...props.map((p) => this.outbox(`${p.page_id}/${p.key}`, {}, hlc, 'page_prop')),
      ...settings.map((s) => this.outbox(s.key, {}, hlc, 'setting')),
      ...attachments.map((a) => this.outbox(a.id, {}, hlc, 'attachment')),
      { sql: `UPDATE page_updates SET synced = 0`, params: [] },
      { sql: `UPDATE attachments SET uploaded = 0 WHERE deleted = 0`, params: [] },
      { sql: `DELETE FROM kv WHERE key = 'needs_resync'`, params: [] },
    ]);
  }

  /** Listen for changes; `topics` limits which changes wake the listener (default: all). */
  subscribe(fn: Listener, topics: Topic[] = ALL_TOPICS): () => void {
    this.listeners.set(fn, new Set(topics));
    return () => this.listeners.delete(fn);
  }

  /** Wake listeners for changes stored outside the tables (e.g. key/value settings). */
  notifyChange(topics: Topic[]): void {
    for (const [fn, wanted] of this.listeners) {
      if (topics.some((t) => wanted.has(t))) fn();
    }
  }

  private async commit(statements: Statement[], topics: Topic[] = ['pages']): Promise<void> {
    await this.db.batch(statements);
    for (const [fn, wanted] of this.listeners) {
      if (topics.some((t) => wanted.has(t))) fn();
    }
  }

  private outbox(
    entityId: Id,
    patch: Record<string, unknown>,
    hlc: string,
    entity = 'page',
  ): Statement {
    return {
      sql: `INSERT INTO outbox(entity, entity_id, patch, hlc) VALUES (?, ?, ?, ?)`,
      params: [entity, entityId, JSON.stringify(patch), hlc],
    };
  }

  /** Record which page fields changed at `hlc` (per-field merge when syncing). */
  private stamp(id: Id, fields: PageField[], hlc: string): Statement {
    return {
      sql: `UPDATE pages SET field_hlc = json_set(field_hlc, ${fields.map(() => '?, ?').join(', ')}) WHERE id = ?`,
      params: [...fields.flatMap((f) => [`$.${f}`, hlc]), id],
    };
  }

  private async subtree(id: Id): Promise<Id[]> {
    return (await this.db.query<{ id: string }>(SUBTREE_SQL, [id])).map((r) => r.id);
  }

  // ---- queries -------------------------------------------------------------

  /**
   * `workspaceId` filters by shared workspace when given (including explicit
   * `null` for private pages); omitted, every page the device knows about is
   * returned (existing callers that are not workspace-aware keep working
   * unchanged across both private and workspace pages).
   */
  async listPages(
    opts: { trashed?: boolean; workspaceId?: string | null } = {},
  ): Promise<PageRecord[]> {
    const conditions = [`deleted = 0`, `trashed_at IS ${opts.trashed ? 'NOT NULL' : 'NULL'}`];
    const params: SqlValue[] = [];
    if (opts.workspaceId !== undefined) {
      conditions.push(`workspace_id IS ?`);
      params.push(opts.workspaceId);
    }
    const rows = await this.db.query<PageRow>(
      `SELECT * FROM pages WHERE ${conditions.join(' AND ')} ORDER BY sort_key`,
      params,
    );
    return rows.map(toPage);
  }

  async getPage(id: Id): Promise<PageRecord | null> {
    const [row] = await this.db.query<PageRow>(`SELECT * FROM pages WHERE id = ?`, [id]);
    return row ? toPage(row) : null;
  }

  private async lastSortKey(parentId: Id | null): Promise<string | null> {
    const [row] = await this.db.query<{ k: string | null }>(
      `SELECT MAX(sort_key) AS k FROM pages WHERE parent_id IS ? AND deleted = 0`,
      [parentId],
    );
    return row?.k ?? null;
  }

  // ---- mutations -----------------------------------------------------------

  /**
   * `workspaceId` places the new page in a shared workspace (set once, at
   * creation). A subpage with no explicit `workspaceId` inherits its
   * parent's, so adding a page under a workspace page (e.g. from the page
   * tree) keeps it in the same workspace without every call site having to
   * know that.
   */
  async createPage(input: PageInput & { workspaceId?: string }): Promise<PageRecord> {
    const parentId = input.parentId ?? null;
    const workspaceId =
      input.workspaceId ??
      (parentId ? ((await this.getPage(parentId))?.workspaceId ?? null) : null);
    const t = this.now();
    const hlc = this.clock.now();
    const page: PageRecord = {
      id: uuidv7(t),
      parentId,
      title: input.title,
      icon: input.icon ?? null,
      sortKey: keyBetween(await this.lastSortKey(parentId), null),
      createdAt: t,
      updatedAt: t,
      trashedAt: null,
      hlc,
      deleted: false,
      workspaceId,
      lastEditedBy: null,
    };
    await this.commit([
      {
        sql: `INSERT INTO pages(id, parent_id, title, icon, sort_key, created_at, updated_at, hlc, workspace_id)
              VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?)`,
        params: [page.id, parentId, page.title, page.icon, page.sortKey, t, t, hlc, workspaceId],
      },
      this.stamp(page.id, PAGE_FIELDS, hlc),
      this.outbox(page.id, { ...page }, hlc),
    ]);
    // Links written before this page existed now point at it.
    if (page.title) await this.resolveLinksTo(page.id, [page.title]);
    return page;
  }

  async updatePage(id: Id, patch: Partial<Pick<Page, 'title' | 'icon'>>): Promise<void> {
    const sets: string[] = [];
    const params: (string | null)[] = [];
    if (patch.title !== undefined) {
      sets.push('title = ?');
      params.push(patch.title);
    }
    if (patch.icon !== undefined) {
      sets.push('icon = ?');
      params.push(patch.icon);
    }
    if (sets.length === 0) return;
    const t = this.now();
    const hlc = this.clock.now();
    await this.commit([
      {
        sql: `UPDATE pages SET ${sets.join(', ')}, updated_at = ?, hlc = ? WHERE id = ?`,
        params: [...params, t, hlc, id],
      },
      ...(patch.title !== undefined
        ? [{ sql: `UPDATE page_fts SET title = ? WHERE page_id = ?`, params: [patch.title, id] }]
        : []),
      this.stamp(id, [...(Object.keys(patch) as PageField[]), 'updatedAt'], hlc),
      this.outbox(id, { ...patch, updatedAt: t }, hlc),
    ]);
    if (patch.title) await this.resolveLinksTo(id, [patch.title]);
  }

  /** Move a page under `parentId`, placed after sibling `afterId` (null = first). */
  async movePage(id: Id, parentId: Id | null, afterId: Id | null): Promise<void> {
    if (parentId !== null && (await this.subtree(id)).includes(parentId)) {
      throw new Error('Cannot move a page inside itself');
    }
    const siblings = await this.db.query<{ id: string; sort_key: string }>(
      `SELECT id, sort_key FROM pages
       WHERE parent_id IS ? AND deleted = 0 AND trashed_at IS NULL AND id != ?
       ORDER BY sort_key`,
      [parentId, id],
    );
    const idx = afterId === null ? -1 : siblings.findIndex((s) => s.id === afterId);
    if (afterId !== null && idx === -1) throw new Error('afterId is not a sibling');
    const sortKey = keyBetween(
      siblings[idx]?.sort_key ?? null,
      siblings[idx + 1]?.sort_key ?? null,
    );
    const t = this.now();
    const hlc = this.clock.now();
    await this.commit([
      {
        sql: `UPDATE pages SET parent_id = ?, sort_key = ?, updated_at = ?, hlc = ? WHERE id = ?`,
        params: [parentId, sortKey, t, hlc, id],
      },
      this.stamp(id, ['parentId', 'sortKey', 'updatedAt'], hlc),
      this.outbox(id, { parentId, sortKey, updatedAt: t }, hlc),
    ]);
  }

  /**
   * Share an existing private page -- and its whole subtree -- into a
   * workspace, in place. A one-time move: like `workspace_id` at creation,
   * the server only accepts the null-to-workspace transition once per page
   * (and only with write permission there), so this is a no-op for a page
   * already in a workspace.
   */
  async movePageToWorkspace(id: Id, workspaceId: Id): Promise<void> {
    const page = await this.getPage(id);
    if (!page || page.workspaceId) return;
    const statements: Statement[] = [];
    for (const pid of await this.subtree(id)) {
      statements.push(
        {
          sql: `UPDATE pages SET workspace_id = ? WHERE id = ? AND workspace_id IS NULL`,
          params: [workspaceId, pid],
        },
        this.outbox(pid, { workspaceId }, this.clock.now()),
      );
    }
    await this.commit(statements, ['pages']);
  }

  /** Trash a page and its subtree (recoverable from the trash). */
  async trashPage(id: Id): Promise<void> {
    await this.setTrashed(id, this.now());
  }

  async restorePage(id: Id): Promise<void> {
    await this.setTrashed(id, null);
  }

  private async setTrashed(id: Id, trashedAt: number | null): Promise<void> {
    const t = this.now();
    const statements: Statement[] = [];
    for (const pid of await this.subtree(id)) {
      const hlc = this.clock.now();
      statements.push(
        {
          sql: `UPDATE pages SET trashed_at = ?, updated_at = ?, hlc = ? WHERE id = ?`,
          params: [trashedAt, t, hlc, pid],
        },
        this.stamp(pid, ['trashedAt', 'updatedAt'], hlc),
        this.outbox(pid, { trashedAt, updatedAt: t }, hlc),
      );
    }
    // Restoring a child whose parent stays trashed: re-attach it at the root.
    if (trashedAt === null) {
      const page = await this.getPage(id);
      const parent = page?.parentId ? await this.getPage(page.parentId) : null;
      if (parent?.trashedAt) {
        const hlc = this.clock.now();
        const sortKey = keyBetween(await this.lastSortKey(null), null);
        statements.push(
          {
            sql: `UPDATE pages SET parent_id = NULL, sort_key = ?, hlc = ? WHERE id = ?`,
            params: [sortKey, hlc, id],
          },
          this.stamp(id, ['parentId', 'sortKey'], hlc),
          this.outbox(id, { parentId: null, sortKey }, hlc),
        );
      }
    }
    await this.commit(statements);
  }

  /** Permanently delete a trashed subtree. Tombstones remain so deletes can sync. */
  async purgePage(id: Id): Promise<void> {
    const page = await this.getPage(id);
    if (!page?.trashedAt) throw new Error('Only trashed pages can be deleted permanently');
    const statements: Statement[] = [];
    for (const pid of await this.subtree(id)) {
      const hlc = this.clock.now();
      statements.push(
        {
          sql: `UPDATE pages SET deleted = 1, title = '', icon = NULL, hlc = ? WHERE id = ?`,
          params: [hlc, pid],
        },
        this.stamp(pid, ['deleted', 'title', 'icon'], hlc),
        ...['page_updates', 'page_props', 'page_versions', 'tags', 'page_fts', 'inline_tasks'].map(
          (table) => ({
            sql: `DELETE FROM ${table} WHERE page_id = ?`,
            params: [pid],
          }),
        ),
        { sql: `DELETE FROM links WHERE src_id = ?`, params: [pid] },
        { sql: `UPDATE links SET dst_id = NULL WHERE dst_id = ?`, params: [pid] },
        this.outbox(pid, { deleted: true }, hlc),
      );
    }
    await this.commit(statements, ALL_TOPICS);
  }

  // ---- key/value settings ------------------------------------------------------

  async getSetting(key: string): Promise<string | null> {
    const [row] = await this.db.query<{ value: string }>(`SELECT value FROM kv WHERE key = ?`, [
      key,
    ]);
    return row?.value ?? null;
  }

  async setSetting(key: string, value: string): Promise<void> {
    const statements: Statement[] = [
      {
        sql: `INSERT INTO kv(key, value) VALUES (?, ?)
              ON CONFLICT(key) DO UPDATE SET value = excluded.value`,
        params: [key, value],
      },
    ];
    // Workspace-level settings (special folders) travel with the workspace.
    if (SYNCED_SETTINGS.includes(key)) {
      const hlc = this.clock.now();
      statements.push(
        {
          sql: `INSERT INTO synced_settings(key, value, hlc) VALUES (?, ?, ?)
                ON CONFLICT(key) DO UPDATE SET value = excluded.value, hlc = excluded.hlc`,
          params: [key, value, hlc],
        },
        this.outbox(key, { value }, hlc, 'setting'),
      );
    }
    await this.db.batch(statements);
    // Only synced settings: local ones (like sync cursors) change during a sync.
    if (SYNCED_SETTINGS.includes(key)) this.notifyChange(['settings']);
  }

  // ---- workspaces (shared spaces, Deel B) ------------------------------------
  // Metadata/membership, not document content: the server is always
  // authoritative (no offline edits to reconcile), so a sync round just
  // replaces the local copy wholesale instead of a per-field HLC merge.

  async listWorkspaces(): Promise<WorkspaceRow[]> {
    const rows = await this.db.query<{
      id: string;
      name: string;
      created_by: string;
      created_at: number;
    }>(`SELECT id, name, created_by, created_at FROM workspaces ORDER BY created_at`);
    return rows.map((r) => ({
      id: r.id,
      name: r.name,
      createdBy: r.created_by,
      createdAt: r.created_at,
    }));
  }

  async listWorkspaceMembers(workspaceId: Id): Promise<WorkspaceMemberRow[]> {
    const rows = await this.db.query<{
      user_id: string;
      permission: 'read' | 'write';
      status: 'pending' | 'active';
    }>(`SELECT user_id, permission, status FROM workspace_members WHERE workspace_id = ?`, [
      workspaceId,
    ]);
    return rows.map((r) => ({
      workspaceId,
      userId: r.user_id,
      permission: r.permission,
      status: r.status,
    }));
  }

  /**
   * Remove, on this device only, every page of a workspace not in `keep` (the
   * workspaces this user is an active member of): deleted, or access taken
   * away. Its content, props and derived data go too, and so do unsent
   * changes, which the server would refuse anyway. Nothing is sent: this
   * mirrors the server, it is not a delete. Returns the removed page ids.
   */
  async forgetWorkspacesExcept(keep: string[]): Promise<Id[]> {
    const marks = keep.map(() => '?').join(',');
    const rows = await this.db.query<{ id: Id }>(
      `SELECT id FROM pages WHERE workspace_id IS NOT NULL
       ${keep.length ? `AND workspace_id NOT IN (${marks})` : ''}`,
      keep,
    );
    if (rows.length === 0) return [];
    const ids = rows.map((r) => r.id);
    const chunks: Id[][] = [];
    // In chunks, to stay under SQLite's limit on bound parameters.
    for (let i = 0; i < ids.length; i += 400) chunks.push(ids.slice(i, i + 400));
    const statements: Statement[] = [];
    for (const chunk of chunks) {
      const inList = chunk.map(() => '?').join(',');
      statements.push(
        ...['page_updates', 'page_props', 'page_versions', 'tags', 'page_fts', 'inline_tasks'].map(
          (table) => ({ sql: `DELETE FROM ${table} WHERE page_id IN (${inList})`, params: chunk }),
        ),
        { sql: `DELETE FROM links WHERE src_id IN (${inList})`, params: chunk },
        { sql: `UPDATE links SET dst_id = NULL WHERE dst_id IN (${inList})`, params: chunk },
        {
          sql: `DELETE FROM outbox WHERE (entity = 'page' AND entity_id IN (${inList}))
                OR (entity = 'page_prop' AND substr(entity_id, 1, instr(entity_id, '/') - 1) IN (${inList}))`,
          params: [...chunk, ...chunk],
        },
        // A page that stays (private, or in a workspace you keep) but sat under
        // one of these moves to the top level instead of pointing at nothing.
        {
          sql: `UPDATE pages SET parent_id = NULL WHERE parent_id IN (${inList})
                AND (workspace_id IS NULL ${keep.length ? `OR workspace_id IN (${marks})` : ''})`,
          params: [...chunk, ...keep],
        },
      );
    }
    // Unlink first, so no chunk deletes a parent while a child still points at it.
    for (const chunk of chunks) {
      const inList = chunk.map(() => '?').join(',');
      statements.push({
        sql: `UPDATE pages SET parent_id = NULL WHERE id IN (${inList})`,
        params: chunk,
      });
    }
    for (const chunk of chunks) {
      statements.push({
        sql: `DELETE FROM pages WHERE id IN (${chunk.map(() => '?').join(',')})`,
        params: chunk,
      });
    }
    await this.commit(statements, ALL_TOPICS);
    return ids;
  }

  /** Whether this device has any content for the page (sent or received). */
  async hasContent(pageId: Id): Promise<boolean> {
    const [row] = await this.db.query(`SELECT 1 FROM page_updates WHERE page_id = ? LIMIT 1`, [
      pageId,
    ]);
    return !!row;
  }

  async replaceWorkspaces(
    workspaces: WorkspaceRow[],
    members: WorkspaceMemberRow[],
  ): Promise<void> {
    await this.commit(
      [
        { sql: `DELETE FROM workspace_members` },
        { sql: `DELETE FROM workspaces` },
        ...workspaces.map((w) => ({
          sql: `INSERT INTO workspaces(id, name, created_by, created_at) VALUES (?, ?, ?, ?)`,
          params: [w.id, w.name, w.createdBy, w.createdAt] as SqlValue[],
        })),
        ...members.map((m) => ({
          sql: `INSERT INTO workspace_members(workspace_id, user_id, permission, status) VALUES (?, ?, ?, ?)`,
          params: [m.workspaceId, m.userId, m.permission, m.status] as SqlValue[],
        })),
      ],
      ['workspaces'],
    );
  }

  // ---- page content (Yjs updates) --------------------------------------------

  async loadContent(pageId: Id): Promise<Uint8Array[]> {
    const rows = await this.db.query<{ data: Uint8Array }>(
      `SELECT data FROM page_updates WHERE page_id = ? ORDER BY seq`,
      [pageId],
    );
    return rows.map((r) => r.data);
  }

  /** Append an (already merged) Yjs update and bump the page's updated time. */
  async appendContent(pageId: Id, update: Uint8Array): Promise<void> {
    const t = this.now();
    const hlc = this.clock.now();
    await this.commit(
      [
        {
          sql: `INSERT INTO page_updates(page_id, data, hlc) VALUES (?, ?, ?)`,
          params: [pageId, update, hlc],
        },
        { sql: `UPDATE pages SET updated_at = ? WHERE id = ?`, params: [t, pageId] },
      ],
      ['content'],
    );
  }

  /**
   * Replace all stored updates of a page with one merged update.
   * TODO(Fase 4): only merge rows that are already synced.
   */
  async compactContent(pageId: Id, merged: Uint8Array): Promise<void> {
    const hlc = this.clock.now();
    await this.db.batch([
      { sql: `DELETE FROM page_updates WHERE page_id = ?`, params: [pageId] },
      {
        sql: `INSERT INTO page_updates(page_id, data, hlc) VALUES (?, ?, ?)`,
        params: [pageId, merged, hlc],
      },
    ]);
  }

  // ---- properties --------------------------------------------------------------

  async getProps(pageId: Id): Promise<Record<string, unknown>> {
    const rows = await this.db.query<{ key: string; value: string }>(
      `SELECT key, value FROM page_props WHERE page_id = ? AND deleted = 0 ORDER BY rowid`,
      [pageId],
    );
    return Object.fromEntries(rows.map((r) => [r.key, JSON.parse(r.value) as unknown]));
  }

  /** Every live property with exactly this value (repairs of imported data). */
  async findPropsWithValue(value: unknown): Promise<{ pageId: Id; key: string }[]> {
    return this.db.query<{ pageId: Id; key: string }>(
      `SELECT page_id AS pageId, key FROM page_props WHERE deleted = 0 AND value = ?`,
      [JSON.stringify(value)],
    );
  }

  /** Set a property; `undefined` removes it. */
  async setProp(pageId: Id, key: string, value: unknown): Promise<void> {
    const hlc = this.clock.now();
    const removed = value === undefined;
    await this.commit(
      [
        {
          sql: `INSERT INTO page_props(page_id, key, value, hlc, deleted) VALUES (?, ?, ?, ?, ?)
                ON CONFLICT(page_id, key) DO UPDATE
                SET value = excluded.value, hlc = excluded.hlc, deleted = excluded.deleted`,
          params: [pageId, key, JSON.stringify(removed ? null : value), hlc, removed ? 1 : 0],
        },
        ...(key === 'tags' ? [reindexTags(pageId)] : []),
        this.outbox(
          `${pageId}/${key}`,
          { pageId, key, value: removed ? null : value, deleted: removed },
          hlc,
          'page_prop',
        ),
      ],
      ['props'],
    );
  }

  /** Find a live page by title or alias (case-insensitive); exact title wins. */
  async findPageByName(name: string): Promise<PageRecord | null> {
    const [row] = await this.db.query<PageRow>(
      `SELECT p.* FROM pages p
       WHERE p.deleted = 0 AND p.trashed_at IS NULL AND (
         p.title = ?1 COLLATE NOCASE OR EXISTS (
           SELECT 1 FROM page_props pp, json_each(pp.value) a
           WHERE pp.page_id = p.id AND pp.key = 'aliases' AND pp.deleted = 0
             AND json_type(pp.value) = 'array' AND a.value = ?1 COLLATE NOCASE))
       ORDER BY p.title = ?1 DESC, p.created_at LIMIT 1`,
      [name],
    );
    return row ? toPage(row) : null;
  }

  /** Aliases of every live page, for link autocomplete and the quick switcher. */
  async allAliases(): Promise<Map<Id, string[]>> {
    const rows = await this.db.query<{ page_id: string; value: string }>(
      `SELECT page_id, value FROM page_props WHERE key = 'aliases' AND deleted = 0`,
    );
    const out = new Map<Id, string[]>();
    for (const r of rows) {
      const v = JSON.parse(r.value) as unknown;
      if (Array.isArray(v)) out.set(r.page_id, v.map(String));
    }
    return out;
  }

  // ---- derived index -----------------------------------------------------------

  /** Replace the derived index (links, tags, full text) of one page. */
  async setPageIndex(pageId: Id, index: PageIndex): Promise<void> {
    const page = await this.getPage(pageId);
    const statements: Statement[] = [
      { sql: `DELETE FROM links WHERE src_id = ?`, params: [pageId] },
      { sql: `DELETE FROM tags WHERE page_id = ?`, params: [pageId] },
      { sql: `DELETE FROM page_fts WHERE page_id = ?`, params: [pageId] },
      {
        sql: `INSERT INTO page_fts(page_id, title, body, tags) VALUES (?1, ?2, ?3, ${TAGS_TEXT_SQL})`,
        params: [pageId, page?.title ?? '', index.text],
      },
    ];
    for (const l of index.links) {
      statements.push({
        sql: `INSERT INTO links(src_id, dst_id, dst_title, kind, context) VALUES (?, ?, ?, ?, ?)`,
        params: [pageId, l.dstId, l.dstTitle, l.kind, l.context],
      });
    }
    for (const tag of new Set(index.tags.map((t) => t.toLowerCase()))) {
      statements.push({
        sql: `INSERT INTO tags(page_id, tag) VALUES (?, ?)`,
        params: [pageId, tag],
      });
    }
    await this.commit(statements, ['index']);
  }

  /** For the graph: every resolved link between live pages, and every page's tags. */
  async graphData(): Promise<{
    links: { src: Id; dst: Id }[];
    tags: { pageId: Id; tag: string }[];
  }> {
    const links = await this.db.query<{ src: Id; dst: Id }>(
      `SELECT DISTINCT l.src_id AS src, l.dst_id AS dst
       FROM links l
       JOIN pages a ON a.id = l.src_id AND a.deleted = 0 AND a.trashed_at IS NULL
       JOIN pages b ON b.id = l.dst_id AND b.deleted = 0 AND b.trashed_at IS NULL
       WHERE l.dst_id IS NOT NULL AND l.src_id != l.dst_id`,
    );
    const tags = await this.db.query<{ pageId: Id; tag: string }>(
      `SELECT DISTINCT page_id AS pageId, lower(tag) AS tag FROM tags`,
    );
    return { links, tags };
  }

  async backlinks(pageId: Id): Promise<Backlink[]> {
    return this.db.query<Backlink>(
      `SELECT l.src_id AS pageId, p.title AS title, l.context AS context, l.kind AS kind
       FROM links l JOIN pages p ON p.id = l.src_id
       WHERE l.dst_id = ? AND l.src_id != ? AND p.deleted = 0 AND p.trashed_at IS NULL
       ORDER BY p.updated_at DESC`,
      [pageId, pageId],
    );
  }

  /** Pages whose text mentions one of `names` without linking to `pageId`. */
  async unlinkedMentions(pageId: Id, names: string[]): Promise<Mention[]> {
    const terms = names.map((n) => n.trim()).filter((n) => n.length >= 3);
    if (terms.length === 0) return [];
    const match = terms.map((n) => `"${n.replace(/"/g, '""')}"`).join(' OR ');
    return this.db.query<Mention>(
      `SELECT f.page_id AS pageId, p.title AS title,
              snippet(page_fts, 2, '«', '»', '…', 12) AS snippet
       FROM page_fts f JOIN pages p ON p.id = f.page_id
       WHERE page_fts MATCH ? AND f.page_id != ?
         AND p.deleted = 0 AND p.trashed_at IS NULL
         AND NOT EXISTS (SELECT 1 FROM links l WHERE l.src_id = f.page_id AND l.dst_id = ?)
       LIMIT 50`,
      [`body : (${match})`, pageId, pageId],
    );
  }

  /** Point links that were written before their target existed at that page. */
  async resolveLinksTo(pageId: Id, names: string[]): Promise<void> {
    const valid = names.filter((n) => n.trim());
    if ((await this.pagesLinkingToName(valid)).length === 0) return;
    await this.commit(
      valid.map((n) => ({
        sql: `UPDATE links SET dst_id = ? WHERE dst_id IS NULL AND dst_title = ? COLLATE NOCASE`,
        params: [pageId, n],
      })),
      ['index'],
    );
  }

  /** Source pages holding unresolved links named like `names` (to re-index after a rename). */
  async pagesLinkingToName(names: string[]): Promise<Id[]> {
    if (names.length === 0) return [];
    const rows = await this.db.query<{ src_id: string }>(
      `SELECT DISTINCT src_id FROM links
       WHERE dst_id IS NULL AND dst_title COLLATE NOCASE IN (${names.map(() => '?').join(',')})`,
      names,
    );
    return rows.map((r) => r.src_id);
  }

  async listTags(): Promise<{ tag: string; count: number }[]> {
    return this.db.query(
      `SELECT t.tag AS tag, COUNT(DISTINCT t.page_id) AS count
       FROM tags t JOIN pages p ON p.id = t.page_id
       WHERE p.deleted = 0 AND p.trashed_at IS NULL
       GROUP BY t.tag ORDER BY count DESC, t.tag`,
    );
  }

  /** Pages carrying `tag` or a nested tag below it (`klant` also finds `klant/acme`). */
  async pagesWithTag(tag: string): Promise<PageRecord[]> {
    const t = tag.toLowerCase();
    const rows = await this.db.query<PageRow>(
      `SELECT DISTINCT p.* FROM tags x JOIN pages p ON p.id = x.page_id
       WHERE (x.tag = ? OR substr(x.tag, 1, ?) = ?) AND p.deleted = 0 AND p.trashed_at IS NULL
       ORDER BY p.updated_at DESC`,
      [t, t.length + 1, `${t}/`],
    );
    return rows.map(toPage);
  }

  // ---- tasks ---------------------------------------------------------------------------

  /** Replace the checkbox tasks found in one page's content. */
  async setPageTasks(pageId: Id, tasks: InlineTaskRow[]): Promise<void> {
    await this.commit(
      [
        { sql: `DELETE FROM inline_tasks WHERE page_id = ?`, params: [pageId] },
        ...tasks.map((t) => ({
          sql: `INSERT OR REPLACE INTO inline_tasks(id, page_id, parent_id, position, text, status_char, link_ids)
                VALUES (?, ?, ?, ?, ?, ?, ?)`,
          params: [
            t.id,
            pageId,
            t.parentId,
            t.position,
            t.text,
            t.statusChar,
            JSON.stringify(t.linkIds),
          ],
        })),
      ],
      ['tasks'],
    );
  }

  /** Checkbox tasks of all live pages, in page order. */
  async listInlineTasks(): Promise<InlineTaskRow[]> {
    const rows = await this.db.query<{
      id: string;
      page_id: string;
      parent_id: string | null;
      position: number;
      text: string;
      status_char: string;
      link_ids: string;
    }>(
      `SELECT t.* FROM inline_tasks t JOIN pages p ON p.id = t.page_id
       WHERE p.deleted = 0 AND p.trashed_at IS NULL
       ORDER BY t.page_id, t.position`,
    );
    return rows.map((r) => ({
      id: r.id,
      pageId: r.page_id,
      parentId: r.parent_id,
      position: r.position,
      text: r.text,
      statusChar: r.status_char,
      linkIds: JSON.parse(r.link_ids) as string[],
    }));
  }

  /** Every live page with its properties (for task pages, projects and views). */
  /** Live pages with their properties; with `parentId` only that page's children. */
  async listPagesWithProps(
    opts: { parentId?: Id } = {},
  ): Promise<(PageRecord & { props: Record<string, unknown> })[]> {
    const rows = await this.db.query<PageRow & { props: string | null }>(
      `SELECT p.*, (SELECT json_group_object(pp.key, json(pp.value)) FROM page_props pp
                    WHERE pp.page_id = p.id AND pp.deleted = 0) AS props
       FROM pages p WHERE p.deleted = 0 AND p.trashed_at IS NULL${opts.parentId ? ' AND p.parent_id = ?' : ''}
       ORDER BY p.sort_key`,
      opts.parentId ? [opts.parentId] : [],
    );
    return rows.map((r) => ({
      ...toPage(r),
      props: r.props ? (JSON.parse(r.props) as Record<string, unknown>) : {},
    }));
  }

  // ---- reminders & notifications -------------------------------------------------------

  async reminderStates(): Promise<Map<string, ReminderState>> {
    const rows = await this.db.query<{
      key: string;
      task_id: string;
      status: ReminderState['status'];
      snoozed_until: number | null;
    }>(`SELECT key, task_id, status, snoozed_until FROM reminder_state`);
    return new Map(
      rows.map((r) => [
        r.key,
        { key: r.key, taskId: r.task_id, status: r.status, snoozedUntil: r.snoozed_until },
      ]),
    );
  }

  async setReminderState(state: ReminderState): Promise<void> {
    await this.commit(
      [
        {
          sql: `INSERT INTO reminder_state(key, task_id, status, snoozed_until, updated_at) VALUES (?, ?, ?, ?, ?)
                ON CONFLICT(key) DO UPDATE SET status = excluded.status,
                  snoozed_until = excluded.snoozed_until, updated_at = excluded.updated_at`,
          params: [state.key, state.taskId, state.status, state.snoozedUntil, this.now()],
        },
      ],
      ['notifications'],
    );
  }

  async addNotification(
    n: Omit<NotificationRecord, 'id' | 'createdAt' | 'readAt'>,
  ): Promise<NotificationRecord> {
    const t = this.now();
    const record: NotificationRecord = { ...n, id: uuidv7(t), createdAt: t, readAt: null };
    await this.commit(
      [
        {
          sql: `INSERT INTO notifications(id, key, kind, task_id, page_id, title, body, fire_at, created_at)
                VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?)`,
          params: [record.id, n.key, n.kind, n.taskId, n.pageId, n.title, n.body, n.fireAt, t],
        },
      ],
      ['notifications'],
    );
    return record;
  }

  // ---- AI activity ---------------------------------------------------------------------

  async addAiActivity(
    a: Pick<AiActivity, 'action' | 'summary' | 'pageId' | 'undo'>,
  ): Promise<AiActivity> {
    const t = this.now();
    const record: AiActivity = { ...a, id: uuidv7(t), createdAt: t, undoneAt: null };
    await this.commit(
      [
        {
          sql: `INSERT INTO ai_activity(id, created_at, action, summary, page_id, undo)
                VALUES (?, ?, ?, ?, ?, ?)`,
          params: [record.id, t, a.action, a.summary, a.pageId, JSON.stringify(a.undo)],
        },
      ],
      ['ai'],
    );
    return record;
  }

  async listAiActivity(limit = 50): Promise<AiActivity[]> {
    const rows = await this.db.query<Omit<AiActivity, 'undo'> & { undo: string }>(
      `SELECT id, created_at AS createdAt, action, summary, page_id AS pageId, undo,
              undone_at AS undoneAt
       FROM ai_activity ORDER BY created_at DESC LIMIT ?`,
      [limit],
    );
    return rows.map((r) => ({ ...r, undo: JSON.parse(r.undo) as unknown }));
  }

  async markAiActivityUndone(id: string): Promise<void> {
    await this.commit(
      [{ sql: `UPDATE ai_activity SET undone_at = ? WHERE id = ?`, params: [this.now(), id] }],
      ['ai'],
    );
  }

  async listNotifications(limit = 100): Promise<NotificationRecord[]> {
    return this.db.query<NotificationRecord>(
      `SELECT id, key, kind, task_id AS taskId, page_id AS pageId, title, body,
              fire_at AS fireAt, created_at AS createdAt, read_at AS readAt
       FROM notifications ORDER BY created_at DESC LIMIT ?`,
      [limit],
    );
  }

  /** Mark notifications read (all when `ids` is omitted). */
  async markNotificationsRead(ids?: string[]): Promise<void> {
    if (ids?.length === 0) return;
    const t = this.now();
    await this.commit(
      [
        ids
          ? {
              sql: `UPDATE notifications SET read_at = ? WHERE read_at IS NULL AND id IN (${ids.map(() => '?').join(',')})`,
              params: [t, ...ids],
            }
          : { sql: `UPDATE notifications SET read_at = ? WHERE read_at IS NULL`, params: [t] },
      ],
      ['notifications'],
    );
  }

  /**
   * Remove notifications from the centre (all when `ids` is omitted). Their
   * reminders stay "sent", so they do not come back.
   */
  async deleteNotifications(ids?: string[]): Promise<void> {
    if (ids?.length === 0) return;
    await this.commit(
      [
        ids
          ? {
              sql: `DELETE FROM notifications WHERE id IN (${ids.map(() => '?').join(',')})`,
              params: ids,
            }
          : { sql: `DELETE FROM notifications`, params: [] },
      ],
      ['notifications'],
    );
  }

  /**
   * Keep the centre small: drop read notifications older than `days`, and
   * repeated summaries (one per day is enough; an old bug wrote one per cycle).
   */
  async pruneNotifications(days = 30): Promise<void> {
    await this.commit(
      [
        {
          sql: `DELETE FROM notifications WHERE read_at IS NOT NULL AND created_at < ?`,
          params: [this.now() - days * 86_400_000],
        },
        {
          sql: `DELETE FROM notifications WHERE kind = 'summary' AND id NOT IN
                  (SELECT MIN(id) FROM notifications WHERE kind = 'summary' GROUP BY key)`,
          params: [],
        },
      ],
      ['notifications'],
    );
  }

  // ---- full-text search ------------------------------------------------------------

  /**
   * Full-text search over titles and bodies. Every word must match (as a prefix),
   * title hits rank higher. Diacritics are ignored ("cafe" finds "café").
   */
  async search(query: string, limit = 50): Promise<SearchHit[]> {
    const words = query.match(/[\p{L}\p{N}_]+/gu) ?? [];
    if (words.length === 0) return [];
    const match = words.map((w) => `"${w.replace(/"/g, '""')}"*`).join(' ');
    return this.db.query<SearchHit>(
      `SELECT f.page_id AS pageId, p.title AS title,
              snippet(page_fts, 2, '«', '»', '…', 16) AS snippet
       FROM page_fts f JOIN pages p ON p.id = f.page_id
       WHERE page_fts MATCH ? AND p.deleted = 0 AND p.trashed_at IS NULL
       ORDER BY bm25(page_fts, 0.0, 8.0, 1.0, 8.0)
       LIMIT ?`,
      [match, limit],
    );
  }

  // ---- attachments -------------------------------------------------------------------

  async addAttachment(input: {
    name: string;
    path?: string | null;
    mime: string;
    data: Uint8Array;
  }): Promise<AttachmentInfo> {
    const t = this.now();
    const hlc = this.clock.now();
    const id = uuidv7(t);
    await this.commit(
      [
        {
          sql: `INSERT INTO attachments(id, name, path, mime, size, data, created_at, hlc)
                VALUES (?, ?, ?, ?, ?, ?, ?, ?)`,
          params: [
            id,
            input.name,
            input.path ?? null,
            input.mime,
            input.data.length,
            input.data,
            t,
            hlc,
          ],
        },
        this.outbox(
          id,
          { name: input.name, mime: input.mime, size: input.data.length },
          hlc,
          'attachment',
        ),
      ],
      ['attachments'],
    );
    return {
      id,
      name: input.name,
      path: input.path ?? null,
      mime: input.mime,
      size: input.data.length,
      createdAt: t,
    };
  }

  async listAttachments(): Promise<AttachmentInfo[]> {
    return this.db.query<AttachmentInfo>(
      `SELECT id, name, path, mime, size, created_at AS createdAt FROM attachments
       WHERE deleted = 0 ORDER BY name COLLATE NOCASE`,
    );
  }

  /**
   * Attachment for a link target: exact path ("Bijlagen/a.png") first, then file
   * name ("a.png"), like Obsidian's shortest-path resolution.
   */
  async findAttachment(target: string): Promise<AttachmentInfo | null> {
    const name = target.split('/').pop() ?? target;
    const [row] = await this.db.query<AttachmentInfo>(
      `SELECT id, name, path, mime, size, created_at AS createdAt FROM attachments
       WHERE deleted = 0 AND (path = ?1 COLLATE NOCASE OR name = ?2 COLLATE NOCASE)
       ORDER BY path = ?1 COLLATE NOCASE DESC, length(path) LIMIT 1`,
      [target, name],
    );
    return row ?? null;
  }

  async attachmentData(id: string): Promise<{ mime: string; data: Uint8Array } | null> {
    const [row] = await this.db.query<{ mime: string; data: Uint8Array }>(
      `SELECT mime, data FROM attachments WHERE id = ? AND deleted = 0`,
      [id],
    );
    return row ?? null;
  }

  // ---- versions ------------------------------------------------------------------

  async saveVersion(pageId: Id, state: Uint8Array): Promise<void> {
    const t = this.now();
    await this.commit(
      [
        {
          sql: `INSERT INTO page_versions(id, page_id, created_at, state) VALUES (?, ?, ?, ?)`,
          params: [uuidv7(t), pageId, t, state],
        },
      ],
      ['versions'],
    );
  }

  async listVersions(pageId: Id): Promise<{ id: string; createdAt: number }[]> {
    return this.db.query(
      `SELECT id, created_at AS createdAt FROM page_versions WHERE page_id = ?
       ORDER BY created_at DESC`,
      [pageId],
    );
  }

  async lastVersionAt(pageId: Id): Promise<number | null> {
    const [row] = await this.db.query<{ t: number | null }>(
      `SELECT MAX(created_at) AS t FROM page_versions WHERE page_id = ?`,
      [pageId],
    );
    return row?.t ?? null;
  }

  async getVersion(id: string): Promise<Uint8Array | null> {
    const [row] = await this.db.query<{ state: Uint8Array }>(
      `SELECT state FROM page_versions WHERE id = ?`,
      [id],
    );
    return row?.state ?? null;
  }

  // ---- sync support -------------------------------------------------------------------
  // Records leave as their *current* state with per-field clocks (the outbox only says
  // which ones changed), so repeated or reordered pushes are harmless.

  /** Changed records to push, oldest first, plus the outbox position they cover. */
  async pendingRecords(limit = 500): Promise<{ upTo: number; records: SyncRecord[] }> {
    const rows = await this.db.query<{ seq: number; entity: string; entity_id: string }>(
      `SELECT seq, entity, entity_id FROM outbox ORDER BY seq LIMIT ?`,
      [limit],
    );
    const seen = new Set<string>();
    const records: SyncRecord[] = [];
    for (const r of rows) {
      const key = `${r.entity}:${r.entity_id}`;
      if (seen.has(key)) continue;
      seen.add(key);
      const record = await this.recordFor(r.entity, r.entity_id);
      if (record) records.push(record);
    }
    return { upTo: rows.length ? rows[rows.length - 1]!.seq : 0, records };
  }

  async outboxSize(): Promise<number> {
    const [row] = await this.db.query<{ n: number }>(`SELECT COUNT(*) AS n FROM outbox`);
    return row?.n ?? 0;
  }

  async clearOutbox(upTo: number): Promise<void> {
    await this.db.batch([{ sql: `DELETE FROM outbox WHERE seq <= ?`, params: [upTo] }]);
  }

  private async recordFor(entity: string, id: string): Promise<SyncRecord | null> {
    if (entity === 'page') {
      const [row] = await this.db.query<PageRow & { field_hlc: string }>(
        `SELECT * FROM pages WHERE id = ?`,
        [id],
      );
      if (!row) return null;
      const stored = JSON.parse(row.field_hlc) as Record<string, string>;
      const fields: Record<string, unknown> = {
        parentId: row.parent_id,
        title: row.title,
        icon: row.icon,
        sortKey: row.sort_key,
        createdAt: row.created_at,
        updatedAt: row.updated_at,
        trashedAt: row.trashed_at,
        deleted: row.deleted === 1,
        lastEditedBy: row.last_edited_by,
      };
      // lastEditedBy is only ever set by the server (a trigger on page_updates);
      // echoing an unset local clock as "now" (the row-wide fallback below) could
      // otherwise clobber a real stamp with null on an unrelated push.
      const fieldHlc = Object.fromEntries(
        PAGE_FIELDS.map((f) => [
          f,
          f === 'lastEditedBy' ? (stored[f] ?? '') : (stored[f] ?? row.hlc),
        ]),
      );
      return { kind: 'page', id, fields, fieldHlc, workspaceId: row.workspace_id };
    }
    if (entity === 'page_prop') {
      const [pageId, ...rest] = id.split('/');
      const key = rest.join('/');
      const [row] = await this.db.query<{
        value: string;
        hlc: string;
        deleted: number;
        workspace_id: string | null;
      }>(
        `SELECT pp.value, pp.hlc, pp.deleted, p.workspace_id FROM page_props pp
         LEFT JOIN pages p ON p.id = pp.page_id WHERE pp.page_id = ? AND pp.key = ?`,
        [pageId!, key],
      );
      if (!row) return null;
      const fields = {
        pageId,
        key,
        value: JSON.parse(row.value) as unknown,
        deleted: row.deleted === 1,
      };
      // A property follows its page's workspace, so a shared page's properties
      // (status, due date, database fields, …) are visible to its other members too.
      return {
        kind: 'prop',
        id,
        fields,
        fieldHlc: sameClock(fields, row.hlc),
        workspaceId: row.workspace_id,
      };
    }
    if (entity === 'attachment') {
      const [row] = await this.db.query<{
        name: string;
        path: string | null;
        mime: string;
        size: number;
        created_at: number;
        hlc: string;
        deleted: number;
      }>(`SELECT name, path, mime, size, created_at, hlc, deleted FROM attachments WHERE id = ?`, [
        id,
      ]);
      if (!row) return null;
      const fields = {
        name: row.name,
        path: row.path,
        mime: row.mime,
        size: row.size,
        createdAt: row.created_at,
        deleted: row.deleted === 1,
      };
      return { kind: 'attachment', id, fields, fieldHlc: sameClock(fields, row.hlc) };
    }
    if (entity === 'setting') {
      const [row] = await this.db.query<{ value: string | null; hlc: string }>(
        `SELECT value, hlc FROM synced_settings WHERE key = ?`,
        [id],
      );
      if (!row) return null;
      return { kind: 'setting', id, fields: { value: row.value }, fieldHlc: { value: row.hlc } };
    }
    return null;
  }

  /**
   * Merge a record from the cloud: per field, the newer clock wins. Returns whether
   * anything changed locally. Never writes to the outbox (it came from the cloud).
   */
  /**
   * The server hands out records in the order of their last change, so a
   * property, content or child can arrive before its page (e.g. a page made on
   * another device and renamed after). A hidden placeholder (deleted, oldest
   * clock) satisfies the foreign key; the page's own record fills it in.
   */
  private async ensurePageRow(id: unknown): Promise<void> {
    if (typeof id !== 'string' || !id) return;
    await this.db.batch([
      {
        sql: `INSERT OR IGNORE INTO pages(id, parent_id, title, icon, sort_key, created_at,
                updated_at, trashed_at, deleted, hlc, field_hlc)
              VALUES (?, NULL, '', NULL, 'V', 0, 0, NULL, 1, '', '{}')`,
        params: [id],
      },
    ]);
  }

  async applyRemoteRecord(r: SyncRecord): Promise<boolean> {
    for (const h of Object.values(r.fieldHlc)) {
      try {
        this.clock.receive(h);
      } catch {
        // A clock far in the future (bad device time) must not stall sync.
      }
    }
    if (r.kind === 'page') return this.applyRemotePage(r);
    if (r.kind === 'prop') {
      const { pageId, key, value, deleted } = r.fields as {
        pageId: string;
        key: string;
        value: unknown;
        deleted: boolean;
      };
      const hlc = maxClock(r.fieldHlc);
      const [cur] = await this.db.query<{ hlc: string }>(
        `SELECT hlc FROM page_props WHERE page_id = ? AND key = ?`,
        [pageId, key],
      );
      if (cur && cur.hlc >= hlc) return false;
      await this.ensurePageRow(pageId);
      await this.commit(
        [
          {
            sql: `INSERT INTO page_props(page_id, key, value, hlc, deleted) VALUES (?, ?, ?, ?, ?)
                  ON CONFLICT(page_id, key) DO UPDATE
                  SET value = excluded.value, hlc = excluded.hlc, deleted = excluded.deleted`,
            params: [pageId, key, JSON.stringify(value ?? null), hlc, deleted ? 1 : 0],
          },
          ...(key === 'tags' ? [reindexTags(pageId)] : []),
        ],
        ['props'],
      );
      return true;
    }
    if (r.kind === 'setting') {
      const hlc = maxClock(r.fieldHlc);
      const [cur] = await this.db.query<{ hlc: string }>(
        `SELECT hlc FROM synced_settings WHERE key = ?`,
        [r.id],
      );
      if (cur && cur.hlc >= hlc) return false;
      const value = (r.fields.value as string | null) ?? null;
      await this.db.batch([
        {
          sql: `INSERT INTO synced_settings(key, value, hlc) VALUES (?, ?, ?)
                ON CONFLICT(key) DO UPDATE SET value = excluded.value, hlc = excluded.hlc`,
          params: [r.id, value, hlc],
        },
        value === null
          ? { sql: `DELETE FROM kv WHERE key = ?`, params: [r.id] }
          : {
              sql: `INSERT INTO kv(key, value) VALUES (?, ?)
                    ON CONFLICT(key) DO UPDATE SET value = excluded.value`,
              params: [r.id, value],
            },
      ]);
      // Special folders and reminder rules: views that read settings refresh.
      this.notifyChange(['pages', 'notifications', 'settings']);
      return true;
    }
    return false; // attachments: see applyRemoteAttachment (needs the file)
  }

  private async applyRemotePage(r: SyncRecord): Promise<boolean> {
    const [row] = await this.db.query<PageRow & { field_hlc: string }>(
      `SELECT * FROM pages WHERE id = ?`,
      [r.id],
    );
    const local: Record<string, string> = row
      ? (JSON.parse(row.field_hlc) as Record<string, string>)
      : {};
    const columns: Record<PageField, string> = {
      parentId: 'parent_id',
      title: 'title',
      icon: 'icon',
      sortKey: 'sort_key',
      createdAt: 'created_at',
      updatedAt: 'updated_at',
      trashedAt: 'trashed_at',
      deleted: 'deleted',
      lastEditedBy: 'last_edited_by',
    };
    const value = (f: PageField): SqlValue => {
      const v = r.fields[f];
      if (f === 'deleted') return v ? 1 : 0;
      return (v ?? null) as SqlValue;
    };
    // Its parent may not have arrived yet either.
    if (r.fields.parentId) await this.ensurePageRow(r.fields.parentId);
    if (!row) {
      const f = r.fields;
      await this.commit(
        [
          {
            sql: `INSERT INTO pages(id, parent_id, title, icon, sort_key, created_at, updated_at,
                    trashed_at, deleted, hlc, field_hlc, workspace_id, last_edited_by)
                  VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
            params: [
              r.id,
              (f.parentId as string | null) ?? null,
              (f.title as string) ?? '',
              (f.icon as string | null) ?? null,
              (f.sortKey as string) ?? 'V',
              (f.createdAt as number) ?? this.now(),
              (f.updatedAt as number) ?? this.now(),
              (f.trashedAt as number | null) ?? null,
              f.deleted ? 1 : 0,
              maxClock(r.fieldHlc),
              JSON.stringify(r.fieldHlc),
              r.workspaceId ?? null,
              (f.lastEditedBy as string | null) ?? null,
            ],
          },
        ],
        ['pages'],
      );
      return true;
    }
    // Set once, from whichever record (placeholder or real) first carries it.
    const setWorkspace = row.workspace_id === null && !!r.workspaceId;
    const newer = PAGE_FIELDS.filter(
      (f) => r.fieldHlc[f] && r.fieldHlc[f]! > (local[f] ?? row.hlc),
    );
    if (newer.length === 0 && !setWorkspace) return false;
    const merged = { ...local, ...Object.fromEntries(newer.map((f) => [f, r.fieldHlc[f]!])) };
    const statements: Statement[] = [
      {
        sql: `UPDATE pages SET ${newer.map((f) => `${columns[f]} = ?`).join(', ')}${
          newer.length ? ', ' : ''
        }hlc = ?, field_hlc = ?${setWorkspace ? ', workspace_id = ?' : ''}
              WHERE id = ?`,
        params: [
          ...newer.map(value),
          maxClock(merged),
          JSON.stringify(merged),
          ...(setWorkspace ? [r.workspaceId ?? null] : []),
          r.id,
        ],
      },
    ];
    if (newer.includes('title')) {
      statements.push({
        sql: `UPDATE page_fts SET title = ? WHERE page_id = ?`,
        params: [(r.fields.title as string) ?? '', r.id],
      });
    }
    await this.commit(statements, ['pages']);
    return true;
  }

  async hasAttachment(id: string): Promise<boolean> {
    const [row] = await this.db.query(`SELECT 1 FROM attachments WHERE id = ?`, [id]);
    return !!row;
  }

  /** Store an attachment received from the cloud (metadata + downloaded file). */
  async applyRemoteAttachment(r: SyncRecord, data: Uint8Array): Promise<void> {
    const f = r.fields as {
      name: string;
      path: string | null;
      mime: string;
      size: number;
      createdAt: number;
      deleted: boolean;
    };
    await this.commit(
      [
        {
          sql: `INSERT INTO attachments(id, name, path, mime, size, data, created_at, hlc, deleted, uploaded)
                VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, 1)
                ON CONFLICT(id) DO UPDATE SET deleted = excluded.deleted, hlc = excluded.hlc`,
          params: [
            r.id,
            f.name,
            f.path,
            f.mime,
            f.size,
            data,
            f.createdAt,
            maxClock(r.fieldHlc),
            f.deleted ? 1 : 0,
          ],
        },
      ],
      ['attachments'],
    );
  }

  async attachmentsToUpload(limit = 20): Promise<{ id: string; mime: string; data: Uint8Array }[]> {
    return this.db.query(
      `SELECT id, mime, data FROM attachments WHERE uploaded = 0 AND deleted = 0 LIMIT ?`,
      [limit],
    );
  }

  async markAttachmentUploaded(id: string): Promise<void> {
    await this.db.batch([
      { sql: `UPDATE attachments SET uploaded = 1 WHERE id = ?`, params: [id] },
    ]);
  }

  /** Local content updates not yet in the cloud, with the page's workspace (if any). */
  async unsyncedContent(
    limit = 200,
  ): Promise<{ seq: number; pageId: string; data: Uint8Array; workspaceId: string | null }[]> {
    return this.db.query(
      `SELECT pu.seq AS seq, pu.page_id AS pageId, pu.data AS data, p.workspace_id AS workspaceId
       FROM page_updates pu LEFT JOIN pages p ON p.id = pu.page_id
       WHERE pu.synced = 0 ORDER BY pu.seq LIMIT ?`,
      [limit],
    );
  }

  async markContentSynced(seqs: number[]): Promise<void> {
    if (seqs.length === 0) return;
    await this.db.batch([
      {
        sql: `UPDATE page_updates SET synced = 1 WHERE seq IN (${seqs.map(() => '?').join(',')})`,
        params: seqs,
      },
    ]);
  }

  /** Add a content update from the cloud once (de-duplicated by its server id). */
  async applyRemoteContent(remoteId: number, pageId: string, data: Uint8Array): Promise<boolean> {
    const [exists] = await this.db.query(`SELECT 1 FROM page_updates WHERE remote_id = ?`, [
      remoteId,
    ]);
    if (exists) return false;
    await this.ensurePageRow(pageId);
    await this.commit(
      [
        {
          sql: `INSERT INTO page_updates(page_id, data, hlc, synced, remote_id) VALUES (?, ?, ?, 1, ?)`,
          params: [pageId, data, this.clock.now(), remoteId],
        },
      ],
      ['content'],
    );
    return true;
  }
}

/** A shared workspace (Deel B): metadata only, members listed separately. */
export interface WorkspaceRow {
  id: string;
  name: string;
  createdBy: string;
  createdAt: number;
}

export interface WorkspaceMemberRow {
  workspaceId: string;
  userId: string;
  permission: 'read' | 'write';
  /** 'pending' = invited but not yet accepted; doesn't grant data access. */
  status: 'pending' | 'active';
}

export interface SyncRecord {
  kind: 'page' | 'prop' | 'attachment' | 'setting';
  id: string;
  fields: Record<string, unknown>;
  fieldHlc: Record<string, string>;
  /**
   * A shared workspace this record belongs to (page kind only), or undefined/null
   * for a private record. Set once at creation, not per-field HLC merged: the
   * server rejects it changing workspace on update (see RLS/merge_records).
   */
  workspaceId?: string | null;
}

type PageField =
  | 'parentId'
  | 'title'
  | 'icon'
  | 'sortKey'
  | 'createdAt'
  | 'updatedAt'
  | 'trashedAt'
  | 'deleted'
  | 'lastEditedBy';

const PAGE_FIELDS: PageField[] = [
  'parentId',
  'title',
  'icon',
  'sortKey',
  'createdAt',
  'updatedAt',
  'trashedAt',
  'deleted',
  'lastEditedBy',
];

/** Workspace-level settings that sync (special folders, reminder rules); the rest stays per device. */
export const SYNCED_SETTINGS = [
  'tasks_parent_id',
  'templates_parent_id',
  'daily_parent_id',
  'inbox_page_id',
  'clips_parent_id',
  'plugins_parent_id',
  'notification_settings',
  'projects_db_id',
  'meetings_db_id',
  'starter_databases_v1',
  'starter_databases_v2',
  'starter_databases_v3',
  'dashboard_layout',
  /** Pages starred in the sidebar (JSON array of ids). */
  'favorites',
  /** The hidden "Onthouden" folder (ADR 0027). */
  'memos_parent_id',
  'dashboard_v2',
  /** Order and visibility of the sidebar's fixed menu (JSON). */
  'sidebar_menu',
];

const sameClock = (fields: Record<string, unknown>, hlc: string) =>
  Object.fromEntries(Object.keys(fields).map((k) => [k, hlc]));

const maxClock = (clocks: Record<string, string>) =>
  Object.values(clocks).reduce((a, b) => (b > a ? b : a), '');

export interface IndexedLink {
  dstId: Id | null;
  dstTitle: string;
  kind: 'link' | 'embed';
  context: string;
}

export interface PageIndex {
  links: IndexedLink[];
  tags: string[];
  text: string;
}

export interface Backlink {
  pageId: Id;
  title: string;
  context: string;
  kind: 'link' | 'embed';
}

export interface Mention {
  pageId: Id;
  title: string;
  snippet: string;
}

export interface PageNode extends PageRecord {
  children: PageNode[];
}

/** Build the sidebar tree from a flat list; orphans (parent trashed) become roots. */
export function buildTree(pages: PageRecord[]): PageNode[] {
  const byId = new Map<Id, PageNode>();
  for (const p of pages) byId.set(p.id, { ...p, children: [] });
  const roots: PageNode[] = [];
  for (const node of byId.values()) {
    const parent = node.parentId ? byId.get(node.parentId) : undefined;
    (parent ? parent.children : roots).push(node);
  }
  const sort = (nodes: PageNode[]) => {
    nodes.sort((a, b) => (a.sortKey < b.sortKey ? -1 : a.sortKey > b.sortKey ? 1 : 0));
    nodes.forEach((n) => sort(n.children));
  };
  sort(roots);
  return roots;
}

export interface SearchHit {
  pageId: Id;
  title: string;
  snippet: string;
}

export interface AttachmentInfo {
  id: string;
  name: string;
  path: string | null;
  mime: string;
  size: number;
  createdAt: number;
}

export interface InlineTaskRow {
  id: string;
  pageId: Id;
  parentId: string | null;
  position: number;
  text: string;
  statusChar: string;
  linkIds: Id[];
}

export interface ReminderState {
  key: string;
  taskId: string;
  status: 'sent' | 'missed' | 'snoozed' | 'dismissed';
  snoozedUntil: number | null;
}

export interface AiActivity {
  id: string;
  createdAt: number;
  /** The tool that ran, e.g. "create_task". */
  action: string;
  /** What happened, for the log ("Taak 'Offerte sturen' in Inbox"). */
  summary: string;
  pageId: string | null;
  /** Whatever the action needs to be undone. */
  undo: unknown;
  undoneAt: number | null;
}

export interface NotificationRecord {
  id: string;
  key: string;
  kind: 'reminder' | 'missed' | 'summary';
  taskId: string | null;
  pageId: string | null;
  title: string;
  body: string;
  fireAt: number;
  createdAt: number;
  readAt: number | null;
}
